import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtNumber, imgUrl } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { VehicleCard } from "@/components/VehicleCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Search, SlidersHorizontal, X, GitCompare, ArrowRight } from "lucide-react";

const ANY = "__any__";

const SORTS = [
  { v: "newest", l: "Newest First" },
  { v: "price_low", l: "Price: Low to High" },
  { v: "price_high", l: "Price: High to Low" },
  { v: "mileage_low", l: "Mileage: Low to High" },
];

const COLORS = [
  { v: "Black", l: "Black", hex: "#111111" },
  { v: "White", l: "White", hex: "#F8FAFC" },
  { v: "Red", l: "Red", hex: "#D62828" },
  { v: "Blue", l: "Blue", hex: "#1D4ED8" },
  { v: "Metallic Gray", l: "Metallic Gray", hex: "linear-gradient(135deg,#cbd5e1,#64748b)" },
];

export default function Inventory() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [filters, setFilters] = useState({ makes: [], body_types: [], fuel_types: [], transmissions: [], locations: [] });
  const [loading, setLoading] = useState(true);
  const [compareList, setCompareList] = useState([]);
  const [search, setSearch] = useState(params.get("search") || "");

  const [state, setState] = useState({
    make: params.get("make") || ANY,
    body_type: ANY, fuel_type: ANY, transmission: ANY, location: ANY, color: ANY,
    sort: "newest", price: [0, 70000], mileage: 100000, year_min: 2018,
  });

  useEffect(() => { api.get("/vehicles/filters").then((r) => setFilters(r.data)).catch(() => {}); }, []);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (state.make !== ANY) q.set("make", state.make);
    if (state.body_type !== ANY) q.set("body_type", state.body_type);
    if (state.fuel_type !== ANY) q.set("fuel_type", state.fuel_type);
    if (state.transmission !== ANY) q.set("transmission", state.transmission);
    if (state.location !== ANY) q.set("location", state.location);
    if (state.color !== ANY) q.set("color", state.color);
    q.set("sort", state.sort);
    q.set("price_min", state.price[0]);
    q.set("price_max", state.price[1]);
    q.set("mileage_max", state.mileage);
    q.set("year_min", state.year_min);
    try {
      const r = await api.get(`/vehicles?${q.toString()}`);
      setVehicles(r.data);
    } finally { setLoading(false); }
  }, [search, state]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const toggleCompare = (v) => {
    setCompareList((prev) =>
      prev.find((x) => x.id === v.id) ? prev.filter((x) => x.id !== v.id) : prev.length >= 3 ? prev : [...prev, v]
    );
  };

  const set = (k, v) => setState((s) => ({ ...s, [k]: v }));

  const FilterPanel = () => (
    <div className="space-y-6">
      {[
        ["make", "Make", filters.makes],
        ["body_type", "Body Type", filters.body_types],
        ["fuel_type", "Fuel Type", filters.fuel_types],
        ["transmission", "Transmission", filters.transmissions],
        ["location", "Location", filters.locations],
      ].map(([key, label, opts]) => (
        <div key={key}>
          <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</label>
          <Select value={state[key]} onValueChange={(v) => set(key, v)}>
            <SelectTrigger data-testid={`filter-${key}`} className="border-slate-200 bg-[#EEF3FC] text-slate-900"><SelectValue /></SelectTrigger>
            <SelectContent className="border-slate-200 bg-[#FFFFFF] text-slate-900">
              <SelectItem value={ANY}>All {label}s</SelectItem>
              {opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}
      <div>
        <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-600">Color</label>
        <div className="flex flex-wrap gap-2">
          {COLORS.map((c) => {
            const active = state.color === c.v;
            return (
              <button
                key={c.v}
                data-testid={`filter-color-${c.v.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => set("color", active ? ANY : c.v)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${active ? "border-gold bg-gold/10 text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-gold/40"}`}
              >
                <span className="h-4 w-4 rounded-full border border-slate-300" style={{ background: c.hex }} />
                {c.l}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="mb-2 flex justify-between text-xs font-semibold uppercase tracking-wider text-slate-600"><span>Max Price</span><span className="text-gold">{fmtMoney(state.price[1])}</span></label>
        <Slider data-testid="filter-price" min={0} max={70000} step={1000} value={[state.price[1]]} onValueChange={([v]) => set("price", [0, v])} />
      </div>
      <div>
        <label className="mb-2 flex justify-between text-xs font-semibold uppercase tracking-wider text-slate-600"><span>Max Mileage</span><span className="text-gold">{fmtNumber(state.mileage)} mi</span></label>
        <Slider data-testid="filter-mileage" min={0} max={100000} step={5000} value={[state.mileage]} onValueChange={([v]) => set("mileage", v)} />
      </div>
      <Button variant="outline" onClick={() => { setSearch(""); setState({ make: ANY, body_type: ANY, fuel_type: ANY, transmission: ANY, location: ANY, color: ANY, sort: "newest", price: [0, 70000], mileage: 100000, year_min: 2018 }); }} className="w-full border-slate-200 bg-transparent text-slate-700 hover:bg-slate-100 hover:text-slate-900" data-testid="filter-reset">
        Reset Filters
      </Button>
    </div>
  );

  return (
    <Layout>
      <div className="border-b border-slate-200 bg-[#E3ECFB] py-12">
        <div className="container-x">
          <h1 className="font-display text-3xl font-extrabold text-slate-900 lg:text-4xl">Vehicle Inventory</h1>
          <p className="mt-2 text-slate-600">Browse our premium selection of America's most in-demand vehicles.</p>
        </div>
      </div>

      <div className="container-x py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <aside className="hidden lg:block">
            <div className="card-surface sticky top-24 p-6">
              <h3 className="mb-5 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><SlidersHorizontal className="h-4 w-4 text-gold" /> Filters</h3>
              <FilterPanel />
            </div>
          </aside>

          <div className="lg:col-span-3">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <Input data-testid="inventory-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search make, model, or trim…" className="border-slate-200 bg-[#FFFFFF] pl-10 text-slate-900" />
              </div>
              <Select value={state.sort} onValueChange={(v) => set("sort", v)}>
                <SelectTrigger data-testid="inventory-sort" className="w-full border-slate-200 bg-[#FFFFFF] text-slate-900 sm:w-52"><SelectValue /></SelectTrigger>
                <SelectContent className="border-slate-200 bg-[#FFFFFF] text-slate-900">
                  {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant="outline" className="border-slate-200 bg-[#FFFFFF] text-slate-900 lg:hidden" data-testid="mobile-filter-btn"><SlidersHorizontal className="h-4 w-4" /></Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-80 overflow-y-auto border-slate-200 bg-[#EEF3FC] text-slate-900">
                  <h3 className="mb-5 mt-6 font-display text-lg font-bold text-slate-900">Filters</h3>
                  <FilterPanel />
                </SheetContent>
              </Sheet>
            </div>

            <p className="mb-4 text-sm text-slate-600" data-testid="inventory-count">{loading ? "Loading…" : `${vehicles.length} vehicles found`}</p>

            {vehicles.length === 0 && !loading ? (
              <div className="card-surface p-16 text-center text-slate-600">No vehicles match your filters.</div>
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {vehicles.map((v, i) => (
                  <VehicleCard key={v.id} vehicle={v} index={i} compareEnabled compared={!!compareList.find((x) => x.id === v.id)} onCompare={toggleCompare} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Compare bar */}
      {compareList.length > 0 && (
        <div className="fixed bottom-6 left-1/2 z-40 w-[92%] max-w-3xl -translate-x-1/2">
          <div className="glass-strong flex items-center gap-4 rounded-2xl p-3 pl-5 shadow-2xl">
            <GitCompare className="h-5 w-5 text-gold" />
            <div className="flex flex-1 gap-2 overflow-x-auto no-scrollbar">
              {compareList.map((v) => (
                <div key={v.id} className="flex flex-shrink-0 items-center gap-2 rounded-lg bg-slate-100 py-1 pl-1 pr-2">
                  <img src={imgUrl(v.images?.[0]?.url)} alt="" className="h-8 w-12 rounded object-cover" />
                  <span className="text-xs text-slate-900">{v.make} {v.model}</span>
                  <button onClick={() => toggleCompare(v)} className="text-slate-600 hover:text-slate-900"><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <Button data-testid="compare-go-btn" disabled={compareList.length < 2} onClick={() => navigate(`/compare?ids=${compareList.map((v) => v.id).join(",")}`)} className="btn-gold rounded-full px-5">
              Compare <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}
